import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { MessageCircle, Search, Plus, X } from "lucide-react";
import { lmsGetStaffChatThreads, lmsGetStudents, lmsMarkChatThreadRead } from "../api/lmsApi";
import ChatThreadPanel from "../components/ChatThreadPanel";
import "../lms.css";

const initials = (first, last) =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";

const formatListTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

// A teacher's identity is resolved the same way every other teacher-side
// LMS page does; the school admin is a single account per school (its own
// id doubles as school_id, same convention every admin LMS page uses).
const getIdentity = (staffType) => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const schoolId = ud.school_id || ud.admin_id || ud.id || null;
    const staffId = staffType === "teacher" ? ud.teacher_id || ud.id || null : schoolId;
    return { schoolId, staffId };
  } catch {
    return { schoolId: null, staffId: null };
  }
};

// New-chat picker — search + pick any student in the school to start a
// fresh thread with, even one with no messages yet.
function NewChatModal({ students, onSelect, onClose }) {
  const [search, setSearch] = useState("");
  const filtered = students.filter((s) =>
    `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="crs-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "420px" }}>
        <div className="crs-modal-header">
          <span>New Chat</span>
          <button className="ms2-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="crs-modal-body">
          <div style={{ position: "relative", marginBottom: "10px" }}>
            <Search
              size={14}
              style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
            />
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                width: "100%",
                padding: "8px 10px 8px 30px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "13px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
            {filtered.length === 0 ? (
              <div style={{ fontSize: "13px", color: "#94a3b8", textAlign: "center", padding: "12px 0" }}>
                No students found.
              </div>
            ) : (
              filtered.map((s) => (
                <div
                  key={s.id}
                  onClick={() => onSelect(s)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "#2563eb",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {initials(s.first_name, s.last_name)}
                  </div>
                  <span style={{ fontSize: "13px" }}>
                    {s.first_name} {s.last_name}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Chat panel — embedded as the right half of the combined Notice Board /
// Chat page (NoticeBoard renders this directly for both role="teacher" and
// role="admin", there's no separate route for it). Left: contact list of
// students this staff member has an active thread with (sorted by most
// recent activity), plus a "New Chat" picker to start with anyone else.
// Right: the selected
// student's live 1:1 conversation.
export default function StaffChat({ staffType }) {
  const { schoolId, staffId } = useMemo(() => getIdentity(staffType), [staffType]);

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeStudent, setActiveStudent] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [allStudents, setAllStudents] = useState([]);

  const fetchThreads = async () => {
    try {
      const res = await lmsGetStaffChatThreads({ staff_type: staffType, staff_id: staffId });
      setThreads(res.data?.data || []);
    } catch (err) {
      console.error("getStaffChatThreads error:", err);
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (staffId) fetchThreads();
  }, [staffId, staffType]);

  useEffect(() => {
    if (!schoolId) return;
    lmsGetStudents({ school_id: schoolId })
      .then((res) => setAllStudents(res.data?.data || res.data || []))
      .catch((err) => console.error("getStudents error:", err));
  }, [schoolId]);

  const openThread = (t) => {
    setActiveStudent({
      student_id: t.student_id,
      first_name: t.first_name,
      last_name: t.last_name,
      admission_number: t.admission_number,
    });
    if (t.unread_count > 0) {
      lmsMarkChatThreadRead({
        student_id: t.student_id,
        staff_type: staffType,
        staff_id: staffId,
        reader_type: staffType,
      })
        .then(() =>
          setThreads((prev) =>
            prev.map((x) => (x.student_id === t.student_id ? { ...x, unread_count: 0 } : x))
          )
        )
        .catch((err) => console.error("markChatThreadRead error:", err));
    }
  };

  const startNewChat = (student) => {
    setShowNewChat(false);
    setActiveStudent({
      student_id: student.id,
      first_name: student.first_name,
      last_name: student.last_name,
      admission_number: student.admission_number,
    });
  };

  const filteredThreads = threads.filter((t) =>
    `${t.first_name} ${t.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div
        style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <h2 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 700, margin: 0 }}>
          <MessageCircle className="text-blue-600" size={18} />
          Chat
        </h2>
        <button
          type="button"
          className="std-btn-primary"
          onClick={() => setShowNewChat(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <Plus size={14} /> New Chat
        </button>
      </div>

      {showNewChat && (
        <NewChatModal students={allStudents} onSelect={startNewChat} onClose={() => setShowNewChat(false)} />
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>Loading chats...</div>
      ) : (
        <div
          style={{
            display: "flex",
            height: "65vh",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Contact list */}
          <div
            style={{
              width: "240px",
              flexShrink: 0,
              borderRight: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "12px", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                />
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 30px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "13px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredThreads.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                  No chats yet — start a new one.
                </div>
              ) : (
                filteredThreads.map((t) => {
                  const isActive = activeStudent?.student_id === t.student_id;
                  return (
                    <div
                      key={t.student_id}
                      onClick={() => openThread(t)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        cursor: "pointer",
                        background: isActive ? "#eff6ff" : "transparent",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          background: "#2563eb",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "13px",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {initials(t.first_name, t.last_name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#0f172a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.first_name} {t.last_name}
                          </span>
                          <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0, marginLeft: "6px" }}>
                            {formatListTime(t.last_message_at)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.last_message || "No messages yet"}
                        </div>
                      </div>
                      {t.unread_count > 0 && (
                        <span
                          style={{
                            minWidth: "18px",
                            height: "18px",
                            borderRadius: "999px",
                            background: "#2563eb",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 5px",
                            flexShrink: 0,
                          }}
                        >
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Active chat */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {!activeStudent ? (
              <div
                style={{
                  margin: "auto",
                  color: "#94a3b8",
                  fontSize: "13px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <MessageCircle size={32} color="#cbd5e1" />
                Select a chat, or start a new one.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e2e8f0",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      background: "#2563eb",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    {initials(activeStudent.first_name, activeStudent.last_name)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px" }}>
                      {activeStudent.first_name} {activeStudent.last_name}
                    </div>
                    {activeStudent.admission_number && (
                      <div style={{ fontSize: "11px", color: "#94a3b8" }}>{activeStudent.admission_number}</div>
                    )}
                  </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                  <ChatThreadPanel
                    key={activeStudent.student_id}
                    studentId={activeStudent.student_id}
                    staffType={staffType}
                    staffId={staffId}
                    viewerType={staffType}
                    viewerId={staffId}
                    schoolId={schoolId}
                    fill
                    onMessageSent={fetchThreads}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
